<template lang="pug">
  y-dialog(v-model="showDialog" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="600" persistent scrollable content-class="round-3xl")
    v-card(min-height="90vh")
      v-card-title.pa-4
        v-row(no-gutters align="center")
          v-col(cols="10" offset="1")
            div.text-xl.font-bold.neutral700--text.text-center {{ $t('edit_patient_detail') }}
          v-col(cols="1" align="right")
            v-btn.ml-n3(text icon @click="closeDialog")
              v-icon(color="neutral700" :size="24") mdi-close
      v-divider
      v-card-text(:class="$vuetify.breakpoint.xsOnly ? 'pa-4' : 'pa-8'")
        div.d-flex.justify-center.mt-16(v-if="isFetching")
          v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

        template(v-else-if="appointment")
          appointment-patient(ref="patient" :appointment="appointment")
            template(#title)
              div.text-md.font-semibold.neutral700--text {{ $t('make_change_on_details') }}

      v-divider
      v-card-actions.py-4(:class="$vuetify.breakpoint.xsOnly ? 'px-4' : 'px-8'")
        v-btn.button-md.neutral700--text.px-0(@click="closeDialog" text) {{ $t('cancel') }}
        v-spacer
        v-btn.button-md(:loading="isLoading" color="neutral700" depressed :dark="hasChanges" :disabled="!hasChanges" @click="saveData") {{ $t('save') }}

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AppointmentPatient from '@/components/clinic/appointment/AppointmentPatient.vue'

export default {
  name: 'EditPatientDetails',
  components: { AppointmentPatient },
  props: ['open', 'selectedAppointment'],
  data () {
    return {
      isFetching: false,
      isLoading: false,
      appointment: null,
      initialData: null,
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      tempAppointment: 'clinic/getTempAppointment'
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    },
    hasChanges () {
      if (this.tempAppointment && this.tempAppointment.patient) {
        let patient = {
          name: this.tempAppointment.patient.name,
          phone: this.tempAppointment.patient.phone,
          email: this.tempAppointment.patient.email,
          ic: this.tempAppointment.patient.ic,
        }
        if (!this.checkEmptyAddress(this.tempAppointment.address)) {
          patient.address = {
            address_1: this.tempAppointment.address.address_1,
            address_2: this.tempAppointment.address.address_2,
            postcode: this.tempAppointment.address.postcode,
            city: this.tempAppointment.address.city,
            state: this.tempAppointment.address.state,
            country: this.tempAppointment.address.country,
            name: this.tempAppointment.address.name,
            phone: this.tempAppointment.address.phone
          }
        }
        return JSON.stringify(this.initialData) !== JSON.stringify(patient)
      }
      return false
    }
  },
  mounted () {
    this.fetchAppointment()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchAppointment () {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.company.slug}/appointments/${this.selectedAppointment.id}`)
        .then(resp => {
          this.appointment = resp.data
          this.initialData = {
            name: this.appointment.patient.name,
            phone: this.appointment.patient.phone,
            email: this.appointment.patient.email,
            ic: this.appointment.patient.ic,
          }
          if (!this.checkEmptyAddress(this.appointment.patient.address)) {
            this.initialData.address = {
              address_1: this.appointment.patient.address.address_1,
              address_2: this.appointment.patient.address.address_2,
              postcode: this.appointment.patient.address.postcode,
              city: this.appointment.patient.address.city,
              state: this.appointment.patient.address.state,
              country: this.appointment.patient.address.country,
              name: this.appointment.patient.address.name,
              phone: this.appointment.patient.address.phone
            }
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    saveData () {
      if (!this.$refs.patient.$refs.form.validate()) {
        return
      }
      // Check phone error
      if (this.$refs.patient.$refs.phoneInput.phoneAlertMessage.length > 0) {
        return
      }
      if (!this.tempAppointment) {
        return
      }
      this.isLoading = true
      let data = {
        name: this.tempAppointment.patient.name,
        phone: this.tempAppointment.patient.phone,
        email: this.tempAppointment.patient.email,
        ic: this.tempAppointment.patient.ic,
      }
      if (!this.checkEmptyAddress(this.tempAppointment.address)) {
        data.address = {
          address_1: this.tempAppointment.address.address_1,
          address_2: this.tempAppointment.address.address_2,
          postcode: this.tempAppointment.address.postcode,
          city: this.tempAppointment.address.city,
          state: this.tempAppointment.address.state,
          country: this.tempAppointment.address.country,
          name: this.tempAppointment.address.name,
          phone: this.tempAppointment.address.phone
        }
      }
      this.$axios.patch(`/clinics/${this.company.slug}/patients/${this.selectedAppointment.patient}`, data)
        .then(() => {
          this.closeResetDialog()
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
          this.isLoading = false
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    closeResetDialog () {
      this.showDialog = false
      this.$emit('reset')
    },
    closeDialog () {
      this.showDialog = false
    },
    checkEmptyAddress (address) {
      if (!address) {
        return true
      }
      if (Object.keys(address).length === 0) {
        return true
      } else if (Object.values(address).every(val => !val)) {
        return true
      }
      return false
    },
    getAddressInString (addr) {
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    copyText (text) {
      let container = this.$refs.copy
      this.$copyText(text, container)
        .then(() => {
          this.showMessage(['success', this.$t('success_message.content_copied_to_clipboard')])
        })
    }
  },
  destroyed () {
    this.$store.commit('clinic/resetTempAppointment')
  }
}
</script>

<style scoped>
</style>